import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer } from '@robocorp/components';
import { IconTwoCheckmarks } from '@robocorp/icons/iconic';
import { Confirm } from '~/components';
import {
  useDeleteWorkItemsMutation,
  useDiscardWorkItemsMutation,
  useRetryWorkItemsMutation,
  useStartProcessRunWithItemsMutation,
} from '~/queries';
import { WorkItemState } from '~/types/resources';

interface Props {
  workItemId: string;
  processId: string;
  workItemState?: WorkItemState;
  hasWorkItemPayload?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  onDelete?: () => void;
}

export const WorkItemActions: FC<Props> = ({
  children,
  processId,
  workItemId,
  workItemState,
  onDelete,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const {
    mutateAsync: startProcessRunWithItemsAsync,
    isLoading: startProcessRunWithItemsIsLoading,
  } = useStartProcessRunWithItemsMutation();
  const { mutateAsync: retryWorkItemsMutation, isLoading: isRetryWorkItemsLoading } =
    useRetryWorkItemsMutation();
  const { mutateAsync: discardWorkItemsMutation, isLoading: isDiscardWorkItemsLoading } =
    useDiscardWorkItemsMutation();
  const { mutateAsync: deleteWorkItemsMutation, isLoading: isDeleteWorkItemsLoading } =
    useDeleteWorkItemsMutation();

  const onRunWithWorkItem = () =>
    startProcessRunWithItemsAsync({ processId, workItemIds: [workItemId] });
  const onWorkItemDelete = () =>
    deleteWorkItemsMutation({ processId, workItemIds: [workItemId] }).then(onDelete);
  const onWorkItemRetry = () => retryWorkItemsMutation({ processId, workItemIds: [workItemId] });
  const onWorkItemDiscard = () =>
    discardWorkItemsMutation({ processId, workItemIds: [workItemId] });

  return (
    <Confirm title={t('workers.delete')} text={t('workers.deleteText')}>
      {(confirmDelete) => (
        <Drawer.Actions>
          {workItemState === WorkItemState.Failed && (
            <Button
              icon={IconTwoCheckmarks}
              onClick={onWorkItemDiscard}
              disabled={isDiscardWorkItemsLoading || disabled}
              loading={isDiscardWorkItemsLoading}
            >
              {t('workItem.markAsDone')}
            </Button>
          )}
          {children}
          {workItemState === WorkItemState.Failed && (
            <Button
              onClick={onWorkItemRetry}
              disabled={isRetryWorkItemsLoading || disabled}
              loading={isRetryWorkItemsLoading}
              variant="secondary"
            >
              {t('workItem.retry')}
            </Button>
          )}
          {workItemState === WorkItemState.New && (
            <Button
              onClick={onRunWithWorkItem}
              disabled={startProcessRunWithItemsIsLoading}
              variant="secondary"
            >
              {t('workItem.run')}
            </Button>
          )}

          {workItemState !== WorkItemState.InProgress && (
            <Button
              onClick={confirmDelete(onWorkItemDelete)}
              disabled={isDeleteWorkItemsLoading}
              variant="secondary"
            >
              {t('workItem.delete')}
            </Button>
          )}
        </Drawer.Actions>
      )}
    </Confirm>
  );
};
